import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../auth/adminPermissions";
import RequirePermission from "../../auth/RequirePermission";
import AdminPageHeader from "../../components/midpoint/admin/layout/AdminPageHeader";
import AdminPageLayout from "../../components/midpoint/admin/layout/AdminPageLayout";
import AdminStatePanel from "../../components/midpoint/admin/layout/AdminStatePanel";
import {
  fetchColombiaWeatherSourceDetail,
  fetchColombiaWeatherSourceHealth,
  filterWeatherSources,
} from "../../weather/colombiaWeatherSourceHealthApi";
import WeatherLayerHealthPanel from "../../components/midpoint/admin/weather/health/WeatherLayerHealthPanel";
import WeatherSourceActionButtons from "../../components/midpoint/admin/weather/health/WeatherSourceActionButtons";
import WeatherSourceHealthCheckResult from "../../components/midpoint/admin/weather/health/WeatherSourceHealthCheckResult";
import WeatherSourceIdeamDiscoveryResult from "../../components/midpoint/admin/weather/health/WeatherSourceIdeamDiscoveryResult";
import WeatherSourceHealthTable from "../../components/midpoint/admin/weather/health/WeatherSourceHealthTable";
import WeatherSourceRunHistory from "../../components/midpoint/admin/weather/health/WeatherSourceRunHistory";
import WeatherSourceSummaryCards from "../../components/midpoint/admin/weather/health/WeatherSourceSummaryCards";
import WeatherMonitoringAlertCards from "../../components/midpoint/admin/weather/health/WeatherMonitoringAlertCards";
import WeatherMonitoringAlertTable from "../../components/midpoint/admin/weather/health/WeatherMonitoringAlertTable";
import WeatherRiskProductizationPanel from "../../components/midpoint/admin/weather/health/WeatherRiskProductizationPanel";
import WeatherRadarHealthSection from "../../components/midpoint/admin/weather/health/radar/WeatherRadarHealthSection";
import WeatherRadarCalibrationPanel from "../../components/midpoint/admin/weather/health/radar/WeatherRadarCalibrationPanel";
import WeatherSiataRadarCalibrationPanel from "../../components/midpoint/admin/weather/health/radar/WeatherSiataRadarCalibrationPanel";
import WeatherSourceObservabilitySection from "../../components/midpoint/admin/weather/health/WeatherSourceObservabilitySection";
import WeatherAlertEvidencePanel from "../../components/midpoint/admin/weather/health/WeatherAlertEvidencePanel";
import WeatherSiataReplayPanel from "../../components/midpoint/admin/weather/health/radar/WeatherSiataReplayPanel";
import { useRadarCalibrationState } from "../../hooks/useRadarCalibrationState";
import {
  fetchWeatherMonitoringSummary,
  fetchWeatherOperationalAlerts,
  patchWeatherOperationalAlert,
  runWeatherMonitoringCheck,
} from "../../weather/colombiaWeatherMonitoringApi";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "fresh", label: "Fresh" },
  { value: "stale", label: "Stale" },
  { value: "warning", label: "Warning" },
  { value: "down", label: "Down" },
  { value: "experimental", label: "Experimental" },
  { value: "historical", label: "Historical" },
  { value: "unknown", label: "Unknown" },
  { value: "no_data", label: "No data" },
  { value: "disabled", label: "Disabled" },
];

function WeatherSourceHealthPageContent() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.WEATHER_LAYERS_MANAGE);
  const [radarCalibrationState, updateRadarCalibration] = useRadarCalibrationState();

  const [dashboard, setDashboard] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState(null);
  const [runningAction, setRunningAction] = useState(null);
  const [lastActionResult, setLastActionResult] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceTypeFilter, setSourceTypeFilter] = useState("all");
  const [officialFilter, setOfficialFilter] = useState("all");
  const [liveFilter, setLiveFilter] = useState("all");
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [provider, setProvider] = useState("");
  const [sortBy, setSortBy] = useState("statusSeverity");

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const payload = await fetchWeatherOperationalAlerts({
        status: "all",
        limit: 50,
      });
      setAlerts(payload.alerts || []);
    } catch {
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  const loadMonitoring = useCallback(async () => {
    try {
      const summary = await fetchWeatherMonitoringSummary();
      setMonitoring(summary);
    } catch {
      setMonitoring(null);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const [payload] = await Promise.all([
        fetchColombiaWeatherSourceHealth({
          includeDisabled: "true",
          sortBy,
        }),
        loadMonitoring(),
        loadAlerts(),
      ]);
      setDashboard(payload);
    } catch (error) {
      setDashboard(null);
      setErrorMessage(error.message || "Unable to load Colombia weather source health.");
    } finally {
      setLoading(false);
    }
  }, [sortBy, loadMonitoring, loadAlerts]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const loadSourceDetail = useCallback(async (sourceKey) => {
    if (!sourceKey) {
      setSelectedSource(null);
      return;
    }

    setDetailLoading(true);

    try {
      const detail = await fetchColombiaWeatherSourceDetail(sourceKey);
      setSelectedSource(detail);
    } catch (error) {
      setToast({
        tone: "error",
        message: error.message || "Unable to load source detail.",
      });
      const fallback = dashboard?.sources?.find((row) => row.sourceKey === sourceKey);
      setSelectedSource(fallback || null);
    } finally {
      setDetailLoading(false);
    }
  }, [dashboard?.sources]);

  const filteredSources = useMemo(() => {
    return filterWeatherSources(dashboard?.sources || [], {
      search: search.trim(),
      status: statusFilter,
      sourceType: sourceTypeFilter,
      officialFilter,
      liveFilter,
      enabledOnly,
      provider: provider.trim(),
      sortBy,
      sortDir: "asc",
    });
  }, [
    dashboard?.sources,
    search,
    statusFilter,
    sourceTypeFilter,
    officialFilter,
    liveFilter,
    enabledOnly,
    provider,
    sortBy,
  ]);

  const sourceTypeOptions = useMemo(() => {
    const types = new Set((dashboard?.sources || []).map((source) => source.sourceType));
    return ["all", ...Array.from(types).filter(Boolean)];
  }, [dashboard?.sources]);

  const handleSelectSource = (sourceKey) => {
    loadSourceDetail(sourceKey);
  };

  const sourceAlerts = useMemo(() => {
    if (!selectedSource?.sourceKey) {
      return [];
    }
    return alerts.filter(
      (row) =>
        row.sourceKey === selectedSource.sourceKey ||
        (row.alertType === "both_radar_sources_down" && !row.sourceKey)
    );
  }, [alerts, selectedSource?.sourceKey]);

  const handleAlertStatusChange = async (alert, status) => {
    try {
      await patchWeatherOperationalAlert(alert.alertId, { status });
      await Promise.all([loadAlerts(), loadMonitoring()]);
      setToast({ tone: "success", message: `Alert marked ${status}.` });
    } catch (error) {
      setToast({
        tone: "error",
        message: error.message || "Unable to update alert.",
      });
    }
  };

  const handleRunMonitoring = async () => {
    setRunningAction("monitoring-run");
    try {
      await runWeatherMonitoringCheck({ dryRun: false });
      await Promise.all([loadDashboard(), loadAlerts(), loadMonitoring()]);
      setToast({ tone: "success", message: "Monitoring check completed." });
    } catch (error) {
      setToast({
        tone: "error",
        message: error.message || "Monitoring check failed.",
      });
    } finally {
      setRunningAction(null);
    }
  };

  const handleActionComplete = (result) => {
    setRunningAction(null);
    setLastActionResult(result);
    if (result?.dashboard) {
      setDashboard(result.dashboard);
      if (selectedSource?.sourceKey) {
        const updated = result.dashboard.sources?.find(
          (row) => row.sourceKey === selectedSource.sourceKey
        );
        if (updated) {
          loadSourceDetail(updated.sourceKey);
        }
      }
    } else {
      loadDashboard();
    }
    setToast({ tone: "success", message: "Action completed successfully." });
  };

  return (
    <AdminPageLayout className="admin-source-controls-dash">
      {toast ? (
        <div
          className={`admin-re-toast admin-re-toast--${toast.tone}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      ) : null}

      <AdminPageHeader
        title="Weather Source Health"
        description="Radar pipeline health, Colombia registry sources, layer status, runs, fallback behavior, and operational actions"
        actions={
          <>
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={loadDashboard}
              disabled={loading}
            >
              Refresh
            </button>
            {canManage ? (
              <button
                type="button"
                className="admin-dashboard__action-btn"
                onClick={handleRunMonitoring}
                disabled={loading || runningAction === "monitoring-run"}
              >
                Run monitoring
              </button>
            ) : null}
            <Link to="/admin/weather/scheduler" className="admin-dashboard__action-btn">
              Scheduler
            </Link>
            <Link to="/admin/weather/commercial" className="admin-dashboard__action-btn">
              Commercial
            </Link>
            <Link to="/" className="admin-dashboard__action-btn">
              ← Map
            </Link>
          </>
        }
      />

      <WeatherRadarHealthSection
        canManage={canManage}
        onToast={(toast) => setToast(toast)}
      />

      <WeatherRadarCalibrationPanel
        calibrationState={radarCalibrationState}
        onUpdateState={(patch) => updateRadarCalibration(patch)}
        canManage={canManage}
      />

      <WeatherSiataRadarCalibrationPanel
        canManage={canManage}
        onToast={(toast) => setToast(toast)}
      />

      <WeatherSourceObservabilitySection
        canManage={canManage}
        onToast={(toast) => setToast(toast)}
      />

      <WeatherAlertEvidencePanel canManage={canManage} />

      <WeatherSiataReplayPanel canManage={canManage} onToast={(toast) => setToast(toast)} />

      {dashboard?.fallback?.fallbackActive ? (
        <div className="weather-source-fallback-banner" role="status">
          Registry fallback active: {dashboard.fallback.primarySourceKey} →{" "}
          {dashboard.fallback.fallbackSourceKey}
          {dashboard.fallback.reason ? ` (${dashboard.fallback.reason})` : ""}
        </div>
      ) : null}

      {loading ? (
        <AdminStatePanel variant="loading" message="Fetching Colombia weather source health…" />
      ) : errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={loadDashboard} />
      ) : !dashboard?.sources?.length ? (
        <AdminStatePanel
          variant="empty"
          message="Colombia weather registry sources were not found. Verify migration 121 seed data."
        />
      ) : (
        <>
          <WeatherSourceSummaryCards summary={dashboard.summary} />
          <WeatherMonitoringAlertCards monitoring={monitoring} />
          <WeatherRiskProductizationPanel />

          <div className="admin-dash__card admin-dash__card--wide panel-card">
            <h2 className="admin-section-title">Operational alerts</h2>
            <WeatherMonitoringAlertTable
              alerts={alerts.filter((row) => row.status !== "resolved")}
              loading={alertsLoading}
              canManage={canManage}
              onAcknowledge={(alert) => handleAlertStatusChange(alert, "acknowledged")}
              onResolve={(alert) => handleAlertStatusChange(alert, "resolved")}
              onMute={(alert) => handleAlertStatusChange(alert, "muted")}
            />
          </div>

          <div className="admin-source-controls-dash__toolbar panel-card">
            <label className="admin-source-controls-dash__search">
              <span>Search</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Source name or key"
              />
            </label>
            <label className="admin-source-controls-dash__select">
              <span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-source-controls-dash__select">
              <span>Type</span>
              <select
                value={sourceTypeFilter}
                onChange={(event) => setSourceTypeFilter(event.target.value)}
              >
                {sourceTypeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option === "all" ? "All types" : option.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </label>
            <label className="admin-source-controls-dash__select">
              <span>Official</span>
              <select
                value={officialFilter}
                onChange={(event) => setOfficialFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="official">Official</option>
                <option value="third_party">Third-party</option>
              </select>
            </label>
            <label className="admin-source-controls-dash__select">
              <span>Live / archive</span>
              <select value={liveFilter} onChange={(event) => setLiveFilter(event.target.value)}>
                <option value="all">All</option>
                <option value="live">Live</option>
                <option value="historical">Historical</option>
              </select>
            </label>
            <label className="admin-source-controls-dash__select">
              <span>Sort</span>
              <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                <option value="statusSeverity">Status severity</option>
                <option value="sourceName">Source name</option>
                <option value="lastObservedDataAt">Last observed data</option>
                <option value="freshnessMinutes">Freshness age</option>
              </select>
            </label>
            <label className="admin-source-controls-dash__checkbox">
              <input
                type="checkbox"
                checked={enabledOnly}
                onChange={(event) => setEnabledOnly(event.target.checked)}
              />
              Enabled only
            </label>
            <label className="admin-source-controls-dash__search">
              <span>Provider</span>
              <input
                type="search"
                value={provider}
                onChange={(event) => setProvider(event.target.value)}
                placeholder="IDEAM, SIATA…"
              />
            </label>
          </div>

          <div className="weather-source-health-layout">
            <div className="weather-source-health-main admin-dash__card admin-dash__card--wide">
              <WeatherSourceHealthTable
                sources={filteredSources}
                selectedSourceKey={selectedSource?.sourceKey}
                onSelectSource={handleSelectSource}
                canManage={canManage}
              />
            </div>

            {selectedSource ? (
              <div className="weather-source-health-detail">
                {detailLoading ? (
                  <AdminStatePanel variant="loading" message="Fetching layers and metrics…" />
                ) : (
                  <>
                    <WeatherLayerHealthPanel
                      source={selectedSource}
                      sourceAlerts={sourceAlerts}
                      monitoringRun={monitoring?.latestMonitoringRun}
                      onClose={() => setSelectedSource(null)}
                    />
                    <div className="weather-source-detail__actions panel-card">
                      <h3>Operational actions</h3>
                      <WeatherSourceActionButtons
                        sourceKey={selectedSource.sourceKey}
                        canManage={canManage}
                        runningAction={runningAction}
                        onActionStart={setRunningAction}
                        onActionComplete={handleActionComplete}
                        onActionError={(error) => {
                          setRunningAction(null);
                          setToast({
                            tone: "error",
                            message: error.message || "Action failed.",
                          });
                        }}
                      />
                    </div>
                    {lastActionResult?.sourceKey === selectedSource.sourceKey &&
                    lastActionResult?.action === "check-health" ? (
                      <div className="panel-card">
                        <WeatherSourceHealthCheckResult result={lastActionResult} />
                      </div>
                    ) : null}
                    {lastActionResult?.sourceKey === selectedSource.sourceKey &&
                    lastActionResult?.action === "ideam-discovery" ? (
                      <div className="panel-card">
                        <WeatherSourceIdeamDiscoveryResult result={lastActionResult} />
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </div>

          <WeatherSourceRunHistory runs={dashboard.recentRuns} />
        </>
      )}
    </AdminPageLayout>
  );
}

export default function WeatherSourceHealthPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.WEATHER_LAYERS_VIEW}>
      <WeatherSourceHealthPageContent />
    </RequirePermission>
  );
}
